import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Language } from '../i18n';

interface LanguageStore {
  currentLanguage: Language;
  setLanguage: (language: Language) => void;
  isLanguageLoading: boolean;
  setLanguageLoading: (loading: boolean) => void;
}

export const useLanguageStore = create<LanguageStore>()(
  persist(
    (set) => ({
      currentLanguage: 'en',
      isLanguageLoading: false,
      setLanguage: (language: Language) => {
        // Only set if it's a supported UI language (en or de)
        if (language === 'en' || language === 'de') {
          set({ currentLanguage: language });
          // Update document language attribute for accessibility
          document.documentElement.lang = language;
        } else {
          // Default to English for unsupported languages
          set({ currentLanguage: 'en' });
          document.documentElement.lang = 'en';
        }
      },
      setLanguageLoading: (loading: boolean) => set({ isLanguageLoading: loading }),
    }),
    {
      name: 'language-storage',
      partialize: (state) => ({ currentLanguage: state.currentLanguage }),
    }
  )
);

// Custom hook for translations
export const useTranslation = () => {
  const { currentLanguage } = useLanguageStore();

  const t = (key: string, ...params: any[]): string | string[] => {
    const { t: translate } = require('../i18n');
    return translate(key, currentLanguage, ...params);
  };

  // Helper function for cases where we know the result should be a string
  const tString = (key: string, ...params: any[]): string => {
    const result = t(key, ...params);
    return Array.isArray(result) ? result.join(', ') : result;
  };

  return { t, tString, currentLanguage };
};